import { useState } from 'react';
import { line, Color, Size, t } from '@bms2/shared';
import { Button, TextInput } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import { useDayText } from '../../shell/viewerDates';
import { post } from './api';
import { QUOTES_PATH } from './QuotePanels';
import styles from './DocumentPage.module.css';

/** 🔒 The Quote's total under its Deal's floor, as the page answer carries it (decision 1629). */
export interface QuoteFloorView {
  message: string;
  shortfallCents: number;
  /** The reason that covers this shortfall, or null where none does. */
  acknowledgement: { byName: string; reason: string; at: string } | null;
}

/** 🔒 Where the reason is written, under the Quote's own route (decision 1629). */
export const quoteFloorAcknowledgementPath = (quoteId: string): string => `${QUOTES_PATH}/${quoteId}/floor-acknowledgement`;

/**
 * 🔒 The document floor on the approval block (decision 1629): the arithmetic
 * in the server's own sentence, and one dialog asking the reason a Quote goes
 * out below it. 🔨 No silent pass: the dialog's primary stays greyed until a
 * reason is typed, and the server refuses an empty one too. Once a reason
 * covers the shortfall, the block names who gave it and why.
 */
/**
 * `mayAcknowledge` is the page's own answer: on a held Quote, whether this
 * viewer may approve it; on any other, whether they may write it. The server
 * asks the same two questions and refuses anybody else.
 */
export function QuoteFloorLine({ quoteId, floor, mayAcknowledge, apiBaseUrl = '', onDone, onError, lineId = null }: { quoteId: string; floor: QuoteFloorView | null | undefined; mayAcknowledge: boolean; apiBaseUrl?: string; onDone: (message: string) => void; onError: (message: string) => void; lineId?: string | null }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  // 🔒 The day in the viewer's own zone and date format, through the one hook (T663).
  const dayText = useDayText();
  if (!floor) return null;
  const acknowledge = async () => {
    setBusy(true);
    // 🔒 A line's floor names its line, one reason per floor broken (T920; decision 1695).
    const answer = await post(quoteFloorAcknowledgementPath(quoteId), lineId ? { reason: reason.trim(), lineId } : { reason: reason.trim() }, apiBaseUrl);
    setBusy(false);
    setOpen(false);
    if (!answer.ok) return onError(answer.message);
    setReason('');
    onDone(t('quote.floor.acknowledgedDone'));
  };
  const given = floor.acknowledgement;
  return (
    <>
      <p className={styles.sentence} data-quote-floor={given ? 'acknowledged' : 'open'} {...(lineId ? { 'data-quote-floor-line': lineId } : {})}>
        {line(t(lineId ? 'quote.floor.lineLabel' : 'quote.floor.label'), '·', floor.message)}
        {given ? <> {line(t('quote.floor.acknowledgedBy'), given.byName, '·', dayText(given.at), '·', given.reason)}</> : null}
      </p>
      {!given && mayAcknowledge ? (
        <span data-quote-floor-action="acknowledge" {...(lineId ? { 'data-quote-floor-line': lineId } : {})}>
          <Button size={Size.Small} color={Color.Navy} text={[t('quote.floor.acknowledge')]} actions={[() => setOpen(true)]} disabled={busy} />
        </span>
      ) : null}
      {open ? (
        <Modal
          open
          title={t(lineId ? 'quote.floor.lineTitle' : 'quote.floor.title')}
          onCancel={() => setOpen(false)}
          dataModal="quoteFloor"
          actions={[{ kind: 'primary', label: t('quote.floor.acknowledge'), onAct: () => void acknowledge(), disabled: busy || reason.trim() === '' }, { kind: 'cancel', label: t('modal.cancel'), onAct: () => setOpen(false) }]}
        >
          <p className={modalStyles.fieldLabel}>{floor.message}</p>
          <p className={modalStyles.fieldLabel}>{t(lineId ? 'quote.floor.lineBody' : 'quote.floor.body')}</p>
          <TextInput size={Size.Large} label={t('quote.floor.reason')} value={reason} onChange={setReason} required name="quoteFloorReason" />
        </Modal>
      ) : null}
    </>
  );
}
